import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, CardHeader, Col } from 'reactstrap';
import ReactECharts from 'echarts-for-react';
import axios from 'axios';
import LogoSpinner from '../../Components/Common/LogoSpinner';

const UserGrowth = ({ dateRange, groupBy }) => {
    const [chartData, setChartData] = useState([]);
    const [metadata, setMetadata] = useState({
        total_users: 0,
        new_users_30d: 0,
        growth_rate: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const echartsRef = useRef(null);

    // Helper function to check if data is sample/placeholder data
    const isSampleUserGrowthData = (data) => {
        // Check if data is empty or missing key series
        if (!data || !data.total_users || !data.new_users || !data.active_users) {
            return true;
        }
        
        // Check if all values are zeros, indicating placeholder data
        const allTotalUsersZero = data.total_users?.every(val => val === 0 || val === null);
        const allNewUsersZero = data.new_users?.every(val => val === 0 || val === null);
        const allActiveUsersZero = data.active_users?.every(val => val === 0 || val === null);
        
        return allTotalUsersZero && allNewUsersZero && allActiveUsersZero;
    };

    // Function to fetch data from API (as fallback)
    const fetchDataFromAPI = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const authToken = JSON.parse(sessionStorage.getItem('authUser'))?.token;
            if (!authToken) {
                throw new Error('Authentication token not found');
            }

            const response = await axios.get(`${process.env.REACT_APP_API_URL1}analytics/dashboard/user-growth`, {
                params: {
                    start_date: dateRange.start.toISOString(),
                    end_date: dateRange.end.toISOString(),
                    group_by: groupBy
                },
                headers: {
                    'Authorization': `${authToken}`
                }
            });

            if (response.data) {
                console.log('User Growth API Response:', response.data);
                setChartData(response.data.series || []);
                setMetadata(response.data.metadata || {});
            } else {
                console.warn('API returned unsuccessful response:', response.data);
                setError('Failed to fetch user growth data. Please try again later.');
            }
        } catch (error) {
            console.error('Error fetching user growth data:', error);
            setError(`Failed to fetch user growth data: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Function to check and use WebSocket data if available
    const checkWebSocketData = () => {
        if (window.dashboardData && window.dashboardData.userGrowth) {
            console.log('Using WebSocket data for user growth');
            const wsData = window.dashboardData.userGrowth;
            setChartData(wsData.metrics || []);
            setMetadata(wsData.metadata || {});
            setLoading(false);
            return true;
        }
        return false;
    };

    useEffect(() => {
        // First check if WebSocket data is available
        const hasWebSocketData = checkWebSocketData();
        
        // If no WebSocket data, fetch from API as fallback
        if (!hasWebSocketData) {
            fetchDataFromAPI();
        }
        
        // Set up event listener for WebSocket updates
        const handleUserGrowthUpdate = (data) => {
            console.log('Received user growth update from WebSocket:', data);
            setChartData(data.metrics || []);
            setMetadata(data.metadata || {});
            setLoading(false);
        };
        
        // Add event listener
        if (window.dashboardEvents) {
            window.dashboardEvents.on('dashboard:userGrowth', handleUserGrowthUpdate);
        }
        
        // Clean up
        return () => {
            if (window.dashboardEvents) {
                window.dashboardEvents.off('dashboard:userGrowth', handleUserGrowthUpdate);
            }
        };
    }, [dateRange, groupBy]);

    const getOption = () => ({
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: '#6a7985'
                }
            }
        },
        legend: {
            data: ['Total Users', 'New Users', 'Active Users']
        },
        toolbox: {
            feature: {
                dataZoom: {
                    yAxisIndex: 'none'
                },
                restore: {},
                saveAsImage: {}
            }
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
        },
        xAxis: [
            {
                type: 'category',
                boundaryGap: false,
                data: chartData.timestamps,
                axisLabel: {
                    formatter: (value) => {
                        const date = new Date(value);
                        switch (groupBy) {
                            case 'daily':
                                return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
                            case 'weekly':
                                return `Week ${Math.ceil(date.getDate() / 7)} ${date.toLocaleDateString([], { month: 'short' })}`;
                            case 'monthly':
                                return date.toLocaleDateString([], { month: 'short', year: '2-digit' });
                            case 'yearly':
                                return date.getFullYear().toString();
                            default:
                                return date.toLocaleDateString();
                        }
                    }
                }
            }
        ],
        yAxis: [
            {
                type: 'value',
                name: 'Users',
                position: 'left',
                axisLabel: {
                    formatter: '{value}'
                }
            }
        ],
        series: [
            {
                name: 'Total Users',
                type: 'line',
                stack: 'Total',
                smooth: true,
                lineStyle: {
                    width: 3,
                    shadowColor: 'rgba(0,0,0,0.3)',
                    shadowBlur: 10,
                    shadowOffsetY: 8
                },
                areaStyle: {
                    opacity: 0.3,
                    color: 'rgb(77, 119, 255)'
                },
                emphasis: {
                    focus: 'series'
                },
                data: chartData.total_users
            },
            {
                name: 'New Users',
                type: 'line',
                stack: 'Total',
                smooth: true,
                lineStyle: {
                    width: 2
                },
                areaStyle: {
                    opacity: 0.3,
                    color: 'rgb(10, 187, 135)'
                },
                emphasis: {
                    focus: 'series'
                },
                data: chartData.new_users
            },
            {
                name: 'Active Users',
                type: 'line',
                stack: 'Total',
                smooth: true,
                lineStyle: {
                    width: 2
                },
                areaStyle: {
                    opacity: 0.3,
                    color: 'rgb(255, 159, 67)'
                },
                emphasis: {
                    focus: 'series'
                },
                data: chartData.active_users
            }
        ]
    });

    return (
        <Card>
            <CardHeader className="d-flex align-items-center">
                <h4 className="card-title mb-0 flex-grow-1">User Growth Analytics</h4>
            </CardHeader>
            <CardBody>
                {loading || isSampleUserGrowthData(chartData) ? (
                    <div className="text-center py-4">
                        <LogoSpinner message="Loading user growth data..." />
                    </div>
                ) : (
                    <ReactECharts 
                        ref={echartsRef}
                        option={getOption()} 
                        className="header-chart" 
                        style={{ height: "350px" }}
                    />
                )}
            </CardBody>
        </Card>
    );
};

export default UserGrowth; 